import { useReportYoutubeCampaigns } from './useReportYoutubeCampaigns';
import ReportCampaignsTable from '../CampaignsTable/ReportCampaignsTable';
import YoutubeReportPostsTable from '../../Posts/YoutubeReportPostsTable/YoutubeReportPostsTable';
import useUrlState from 'Hooks/useUrlState';
import { useMemo } from 'react';
import { ExpandedState } from '@tanstack/react-table';
import { useReorderReportCampaigns } from '../hooks/useReorderReportCampaigns';
import { PostUrlClipboardButton } from 'Modules/Plans/CopyToClipboard/PostUrlTable';
import { useCopyReportCampaignPostUrls } from '../../hooks/useCopyReportCampaignPostUrls';
import styles from '../ReportCampaigns.module.css';

type UrlState = Partial<{
    expanded: string;
}>;

const ReportYoutubeCampaigns = () => {
    const [urlState, setUrlState] = useUrlState<UrlState>();
    const {
        rows,
        isLoading: areCampaignsLoading,
        isTeamsLoading,
        isProjectsLoading,
        getIsBrandLoading,
        getIsCampaignStatsLoading,
    } = useReportYoutubeCampaigns();

    const expandedState = useMemo(() => {
        const expanded = urlState?.expanded;
        if (!expanded) {
            return {};
        }
        return expanded.split(',').reduce(
            (acc, campaignId) => {
                acc[campaignId] = true;
                return acc;
            },
            {} as Record<string, boolean>
        );
    }, [urlState]);

    const setExpandedState = (expanded: ExpandedState) => {
        const expandedKeys = Object.keys(expanded).filter((key) => expanded[key as keyof ExpandedState]);
        setUrlState({ expanded: expandedKeys.join(',') });
    };

    const reorderCampaigns = useReorderReportCampaigns('youtube');

    const { isLoading: isCopyingPostUrls, copyReportCampaignPostUrls } = useCopyReportCampaignPostUrls('youtube', rows);

    return (
        <>
            <div className={styles.actions}>
                <PostUrlClipboardButton
                    onClick={copyReportCampaignPostUrls}
                    disabled={!rows.length}
                    isLoading={isCopyingPostUrls}
                />
            </div>
            <ReportCampaignsTable
                data={rows}
                isLoading={areCampaignsLoading}
                expandedState={expandedState}
                onExpandedChange={setExpandedState}
                renderSubComponent={(row) => <YoutubeReportPostsTable campaign={row.original} />}
                meta={{
                    isLoading: areCampaignsLoading,
                    getIsCampaignStatsLoading: (row) => getIsCampaignStatsLoading(row.id),
                    getIsBrandLoading: (row) => getIsBrandLoading(row.id),
                    getIsProjectLoading: () => isProjectsLoading,
                    getIsTeamLoading: () => isTeamsLoading,
                }}
                onDragEnd={reorderCampaigns}
            />
        </>
    );
};

export default ReportYoutubeCampaigns;
